import { observer } from "mobx-react";
import { useStores } from "../../../stores";
import { OverlayRoot } from "./OverlayRoot";
import { Preloader } from "./Preloader";
import { AnimatePresence } from "framer-motion";
import { useEffect } from "react";
import { GrandChartWindow } from "./GrandChartWindow";
import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { removeGrandChartRoute } from "../../../helpers/grandChart/grandChart.helper";

interface GrandChartBasicProps {}

export const GrandChartBasic = observer(({}: GrandChartBasicProps) => {
	const { grandCharts, statisticsStore } = useStores();
	const { selected, modal } = grandCharts;

	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const location = useLocation();

	const hasGroupRoute = location.pathname.includes("/course/");

	useEffect(() => {
		const grandChartId = Number(id);
		if (!id || !Number.isFinite(grandChartId)) {
			// URL без id → закрываем
			if (modal.isShow) {
				modal.close();
			}
			return;
		}
		// если уже открыт тот же — ничего не делаем
		if (selected.id === grandChartId && modal.isShow) {
			return;
		}

		selected.setId(grandChartId);
		modal.open();
	}, [id]);

	useEffect(() => {
		if (!modal.isShow) {
			return;
		}
		const originalOverflow = document.body.style.overflow;
		const originalPaddingRight = document.body.style.paddingRight;
		const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
		document.body.style.overflow = "hidden";
		if (scrollbarWidth > 0) {
			document.body.style.paddingRight = `${scrollbarWidth}px`;
		}
		return () => {
			document.body.style.overflow = originalOverflow;
			document.body.style.paddingRight = originalPaddingRight;
		};
	}, [grandCharts.modal.isShow]);

	useEffect(() => {
		if (!modal.isShow || !selected.id) {
			return;
		}

		// Регистрируем сигнал
		const controller = new AbortController();

		(async () => {
			// Получаем джанные по гранд чарту
			await selected.get(controller.signal);

			// Получаем статистику пользователя по текущему гранд чарту
			await statisticsStore.getByGrandChart(selected.id);
		})();

		return () => {
			controller.abort();
			selected.abortLoading();
		};
	}, [modal.isShow, selected.id]);

	useEffect(() => {
		if (hasGroupRoute) return;
		if (!selected.selectedGrandChartGroupLesson.id) return;
		const timer = window.setTimeout(() => {
			selected.clearSelectedGrandChartGroupLesson();
		}, 220);
		return () => window.clearTimeout(timer);
	}, [hasGroupRoute, selected.selectedGrandChartGroupLesson.id]);

	// Закрытие гранд чарта
	const handleOnCloseGrandChart = () => {
		navigate(removeGrandChartRoute(location.pathname), {
			replace: true,
			state: { fromGrandChartClose: true },
		});
	};

	return (
		<>
			<AnimatePresence
				mode="wait"
				onExitComplete={() => {
					grandCharts.clearSelected();
				}}
			>
				{grandCharts.modal.isShow && (
					<OverlayRoot onClose={handleOnCloseGrandChart}>
						<AnimatePresence mode={"wait"}>
							{selected.isLoading && <Preloader key={"preloader"} text={"Loading Grand Chart..."} />}

							{!selected.isLoading && selected.id > 0 && (
								<>
									<GrandChartWindow key="window" />
									<Outlet />
								</>
							)}
						</AnimatePresence>
					</OverlayRoot>
				)}
			</AnimatePresence>
		</>
	);
});
